/**
 * Field rules written by hand, for forms core-api does not define.
 *
 * fieldRules.generated.ts is extracted from core-api's per-year field definitions and is
 * rewritten whole every time extract-core-api-field-rules.mjs runs. A form that only the
 * e-file app files has no core-api definition to extract, so its rules live here instead, and
 * fieldRules.ts merges the two. Rerunning the extractor cannot drop anything in this file.
 *
 * Same vocabulary as the generated file: `kind` says what the column holds, `required` that a
 * return cannot be built without it, `decimal` that the amount may carry cents, and `label` is
 * what a customer reads in place of the column name.
 *
 * Rules here apply to every tax year the app files (2025 and 2026). A form whose rules differ
 * by year would need a per-year table like the generated one; none does yet.
 */
import type { FormFieldRules } from "./fieldRules.generated";

/**
 * Form 1099-Q, Payments From Qualified Education Programs. Column names are the interface's
 * (forms/1099-Q/interface/1099-Q-interface.ts); the identity columns follow the 2026 NEC
 * layout, so they carry the same kinds NEC's do.
 *
 * Box 2 (earnings) can be negative: its XSD type is USDecimalAmountType, while boxes 1 and 3
 * are USDecimalAmountNNType. checkRecords refuses a negative box 1 or 3 by name.
 */
const FORM_1099_Q: FormFieldRules = {
  FILER_FEDERAL_ID_NUMBER: { kind: "tin", required: true, label: "Filer Federal ID Number" },
  FILER_NAME: { kind: "text", required: true, label: "Filer Name" },
  FILER_NAME2: { kind: "text", label: "Filer Name 2" },
  FILER_ADDRESS1: { kind: "text", required: true, label: "Filer Address 1" },
  FILER_SUITE_NO: { kind: "text", label: "Filer Suite No" },
  FILER_ADDRESS2: { kind: "text", label: "Filer Address 2" },
  FILER_CITY: { kind: "text", required: true, label: "Filer City" },
  FILER_STATE: { kind: "state", required: true, label: "Filer State" },
  FILER_ZIP: { kind: "zip", required: true, label: "Filer ZIP" },
  FILER_COUNTRY: { kind: "text", label: "Filer Country" },
  FILER_PHONE: { kind: "text", label: "Filer Phone" },

  RECIPIENT_ID_TYPE: { kind: "idType", label: "Recipient ID Type" },
  RECIPIENT_ID_NUMBER: { kind: "tin", required: true, label: "Recipient ID Number" },
  RECIPIENT_FIRST_NAME: { kind: "text", label: "Recipient First Name" },
  RECIPIENT_MIDDLE_NAME: { kind: "text", label: "Recipient Middle Name" },
  RECIPIENT_LAST_NAME: { kind: "text", label: "Recipient Last Name" },
  RECIPIENT_SUFFIX: { kind: "text", label: "Recipient Suffix" },
  RECIPIENT_BUSINESS_NAME1: { kind: "text", label: "Recipient Business Name 1" },
  RECIPIENT_BUSINESS_NAME2: { kind: "text", label: "Recipient Business Name 2" },
  RECIPIENT_ADDRESS1: { kind: "text", required: true, label: "Recipient Address 1" },
  RECIPIENT_APT_NO: { kind: "text", label: "Recipient Apt No" },
  RECIPIENT_ADDRESS2: { kind: "text", label: "Recipient Address 2" },
  // City, state and ZIP are required of a US recipient only; checkRecords decides that, since
  // a foreign recipient carries a province and a postal code instead.
  RECIPIENT_CITY: { kind: "text", label: "Recipient City" },
  RECIPIENT_STATE: { kind: "state", label: "Recipient State" },
  RECIPIENT_ZIP: { kind: "zip", label: "Recipient ZIP" },
  FOREIGN_COUNTRY_INDICATOR: { kind: "checkbox", label: "Foreign Country Indicator" },
  FOREIGN_COUNTRY: { kind: "text", label: "Foreign Country" },

  ACCOUNT_NUMBER: { kind: "text", label: "Account Number" },
  BOX1_GROSS_DISTRIBUTION: { kind: "amount", decimal: true, label: "Gross Distribution" },
  BOX2_EARNINGS: { kind: "amount", decimal: true, label: "Earnings" },
  BOX3_BASIS: { kind: "amount", decimal: true, label: "Basis" },
  BOX4A_TRUSTEE_TO_TRUSTEE_TRANSFER: { kind: "checkbox", label: "Trustee-to-Trustee Transfer" },
  BOX4B_QTP_TO_ROTH_IRA_TRANSFER: { kind: "checkbox", label: "QTP to Roth IRA Transfer" },
  BOX5_PRIVATE_QTP: { kind: "checkbox", label: "Private QTP" },
  BOX5_STATE_QTP: { kind: "checkbox", label: "State QTP" },
  BOX5_COVERDELL_ESA: { kind: "checkbox", label: "Coverdell ESA" },
  BOX6_NOT_DESIGNATED_BENEFICIARY: {
    kind: "checkbox",
    label: "Recipient Is Not the Designated Beneficiary",
  },
  DISTRIBUTION_CODE: { kind: "text", label: "Distribution Code" },
  "1099_YEAR": { kind: "text", label: "1099 Year" },
};

/** Hand-written rules by canonical form type. */
export const MANUAL_FIELD_RULES: Record<string, FormFieldRules> = {
  "1099-Q": FORM_1099_Q,
};
